import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useFileUpload } from '../hooks/useFileUpload';
import type { FileUploadResult } from '../hooks/useFileUpload';

interface FileUploadRef {
    clear: () => void;
    confirm: () => Promise<void>;
}

interface FileUploadProps {
    defaultUrl?: string;
    baseURL: string;
    token: string;
    maxFiles?: number;
    label?: string; // Optional label matching DocumentUpload
    onUploadComplete?: (data: FileUploadResult | null) => void;
    onUploadsComplete?: (results: FileUploadResult[]) => void;
    onClear?: () => void;
}

const PdfIcon = () => (
    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
);

const ImageIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const CloudIcon = () => (
    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
);

const SpinnerIcon = () => (
    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

const ChooseFileIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
);

const TrashIcon = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const RetryIcon = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
);

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
    ({ baseURL, token, onUploadComplete, onUploadsComplete, defaultUrl, onClear, maxFiles = 1, label }, ref) => {
        const fileInputRef = useRef<HTMLInputElement>(null);
        const isMulti = maxFiles > 1;

        const {
            items,
            mutate,
            mutateMany,
            retryItem,
            removeItem,
            clearAll,
            confirmAll,
            isPending,
            isSuccess,
            error,
            data,
        } = useFileUpload(baseURL, token);

        const [isDragging, setIsDragging] = useState(false);

        // single-file compat: fire onUploadComplete when the single item succeeds/fails
        useEffect(() => {
            if (onUploadComplete) {
                if (isSuccess && data) {
                    onUploadComplete(data);
                } else if (error && !isPending) {
                    onUploadComplete(null);
                }
            }
        }, [isSuccess, error, isPending, data, onUploadComplete]);

        // fire onUploadsComplete when all items have settled (supports single and multi-file)
        useEffect(() => {
            if (onUploadsComplete) {
                if (items.length === 0) {
                    onUploadsComplete([]);
                } else {
                    const allSettled = items.every(i => i.status === 'success' || i.status === 'error');
                    if (allSettled) {
                        const successes = items.filter(i => i.status === 'success' && i.result).map(i => i.result!);
                        onUploadsComplete(successes);
                    }
                }
            }
        }, [items, onUploadsComplete]);

        const canAddMore = isMulti
            ? items.filter(i => i.status !== 'error').length < maxFiles
            : items.length === 0 && !defaultUrl;

        const handleFiles = (files: FileList | null) => {
            if (!files || files.length === 0) return;
            const accepted = Array.from(files);
            if (isMulti) {
                const current = items.filter(i => i.status !== 'error').length;
                const slots = maxFiles - current;
                const toUpload = accepted.slice(0, slots);
                if (toUpload.length > 0) mutateMany(toUpload);
            } else {
                mutate(accepted[0]);
            }
        };

        const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            handleFiles(e.target.files);
            if (fileInputRef.current) fileInputRef.current.value = '';
        };

        const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragging(false);
            if (!canAddMore) return;
            handleFiles(e.dataTransfer.files);
        };

        const handleClear = () => {
            clearAll();
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (onClear) onClear();
        };

        useImperativeHandle(ref, () => ({
            clear: handleClear,
            confirm: confirmAll,
        }));

        const showDropZone = canAddMore;
        const hasItems = items.length > 0 || !!defaultUrl;

        const getFileName = (url: string) => {
            try {
                return decodeURIComponent(url.split("/").pop() || "Document");
            } catch {
                return url.split("/").pop() || "Document";
            }
        };

        const isPdf = (urlOrName: string) => /\.pdf$/i.test(urlOrName);

        const isUploading = items.some(i => i.status === 'pending');

        return (
            <div className="flex flex-col gap-3 w-full">
                {label && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        {label}
                    </span>
                )}

                {/* File list */}
                {hasItems && (
                    <div className="flex flex-col gap-3">
                        {/* defaultUrl single-file preview */}
                        {defaultUrl && items.length === 0 && (
                            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isPdf(defaultUrl) ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                                        {isPdf(defaultUrl) ? <PdfIcon /> : <ImageIcon />}
                                    </div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <a href={defaultUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-gray-900 dark:text-white truncate hover:text-accent-500 transition-colors" title={getFileName(defaultUrl)}>
                                            {getFileName(defaultUrl)}
                                        </a>
                                        <span className="text-xs text-gray-400 mt-0.5">Currently on file</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                        title="Remove file"
                                    >
                                        <TrashIcon />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Upload items */}
                        {items.map((item) => {
                            let statusColor = 'text-gray-400';
                            if (item.status === 'success') statusColor = 'text-green-500';
                            else if (item.status === 'error') statusColor = 'text-red-500';

                            let statusLabel: string | null = null;
                            if (item.status === 'pending') statusLabel = `${(item.file.size / 1024).toFixed(2)} KB`;
                            else if (item.status === 'success') statusLabel = 'File uploaded successfully';
                            else if (item.status === 'error') statusLabel = 'Failed to upload file';

                            return (
                                <div key={item.localId} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex flex-col gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isPdf(item.file.name) ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                                            {isPdf(item.file.name) ? <PdfIcon /> : <ImageIcon />}
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={item.file.name}>
                                                {item.file.name}
                                            </div>
                                            <span className={`text-xs mt-0.5 ${statusColor}`}>
                                                {statusLabel}
                                            </span>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {item.status === 'pending' && (
                                                <span className="text-xs font-semibold text-accent-500">{item.progress}%</span>
                                            )}
                                            {item.status === 'error' && (
                                                <button
                                                    type="button"
                                                    onClick={() => retryItem(item.localId)}
                                                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-accent-500 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-all"
                                                    title="Retry upload"
                                                >
                                                    <RetryIcon />
                                                </button>
                                            )}
                                            {item.status !== 'pending' && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(item.localId)}
                                                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                                    title="Remove file"
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {item.status === 'pending' && (
                                        <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-accent-500 transition-all duration-300 rounded-full"
                                                style={{ width: `${item.progress}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Dropzone */}
                {showDropZone && (
                    <>
                        <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            multiple={isMulti}
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            disabled={isUploading && !isMulti}
                        />
                        <div
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            className={`relative border-2 border-dashed rounded-xl py-6 px-4 flex flex-col items-center gap-2 transition-all cursor-pointer ${isDragging
                                    ? "border-accent-500 bg-accent-50 dark:bg-accent-900/10"
                                    : "border-gray-200 dark:border-gray-700 hover:border-accent-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                }`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {/* Cloud upload icon */}
                            <div className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                <CloudIcon />
                            </div>

                            <div className="flex flex-col items-center gap-0.5 text-center">
                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {hasItems ? "Upload another certificate" : "Upload certificate"}
                                </span>
                                <span className="text-xs text-gray-400">
                                    PDF, JPG or PNG (max 5MB)
                                </span>
                            </div>

                            {/* Choose file button */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                                disabled={isUploading && !isMulti}
                                className={`mt-1 flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 cursor-pointer transition-all hover:border-accent-500 hover:text-accent-500 shadow-sm ${isUploading && !isMulti ? "opacity-50 pointer-events-none" : ""
                                    }`}
                            >
                                {isUploading && !isMulti ? (
                                    <>
                                        <SpinnerIcon />
                                        Uploading…
                                    </>
                                ) : (
                                    <>
                                        <ChooseFileIcon />
                                        {isMulti ? "Choose files" : "Choose file"}
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }
);

export default FileUpload;
export { FileUpload };
export type { FileUploadProps, FileUploadRef, FileUploadResult };